import {ChangeDetectionStrategy, Component, inject, OnInit, signal} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {ProgressSpinner} from 'primeng/progressspinner';
import {EMPTY, switchMap, tap} from 'rxjs';
import {redirectToUrl} from '../../utils';
import {environment} from '../../../../environments/environment';
import {MessageService} from 'primeng/api';
import {UserService} from "../../services/user.service";
import {PaymentService} from "../../services/payment/payment.service";

@Component({
  selector: 'homey-resubscribe-redirect-page',
  standalone: true,
  imports: [ProgressSpinner, RouterLink],
  templateUrl: './resubscribe-redirect-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResubscribeRedirectPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly userService = inject(UserService);
  private readonly paymentService = inject(PaymentService);

  protected readonly error = signal<string | null>(null);
  protected readonly fallbackUrl = signal<string | null>(null);

  ngOnInit(): void {
    this.userService.user$.pipe(
      switchMap(user => {
        if (user.status !== 'unauthorized') {
          return this.paymentService.openResubscribeDialog();
        }

        const id = this.route.snapshot.queryParamMap.get('id');
        if (!id) {
          this.error.set('Invalid link. Please check your email or SMS for the correct link.');
          return EMPTY;
        }

        return this.http.get<{ url: string }>(`${environment.apiUrl}/api/public/payment/resubscribe`, {params: {id}}).pipe(
          switchMap(response => redirectToUrl(response.url))
        );
      }),
      tap(url => {
        if (url) {
          setTimeout(() => this.fallbackUrl.set(url), 2000);
        }
      })
    ).subscribe({
      error: (e: HttpErrorResponse) => {
        if (e.status === 409) {
          this.messageService.add({
            severity: 'info',
            summary: 'Already subscribed',
            detail: 'You already have an active subscription.',
          });
          void this.router.navigate(['/']);
        } else {
          this.error.set('Something went wrong. Please try again or contact support.');
        }
      }
    });
  }
}
